"use client";

import { createContext, useContext, useSyncExternalStore, ReactNode } from "react";

import { getSessionItem, setSessionItem } from "@/src/lib/session-storage";

// Persisted so Power Mode survives the hard reloads Next.js performs on deployment changes
const POWER_MODE_STORAGE_KEY = "power-mode-enabled";

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("storage", listener);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

function getSnapshot() {
  return getSessionItem(POWER_MODE_STORAGE_KEY) === "true";
}

interface PowerModeContextType {
  isPowerModeEnabled: boolean;
  togglePowerMode: () => void;
}

const PowerModeContext = createContext<PowerModeContextType | undefined>(undefined);

export function PowerModeProvider({ children }: { children: ReactNode }) {
  // The server always renders Power Mode off; the stored value is applied after hydration.
  const isPowerModeEnabled = useSyncExternalStore(subscribe, getSnapshot, () => false);

  const togglePowerMode = () => {
    setSessionItem(POWER_MODE_STORAGE_KEY, String(!isPowerModeEnabled));
    listeners.forEach((listener) => listener());
  };

  return (
    <PowerModeContext.Provider value={{ isPowerModeEnabled, togglePowerMode }}>{children}</PowerModeContext.Provider>
  );
}

export function usePowerMode() {
  const context = useContext(PowerModeContext);

  if (context === undefined) {
    throw new Error("usePowerMode must be used within a PowerModeProvider");
  }

  return context;
}
